import { QuoteStatus, QuoteStatusLabels } from '@navio/nsdk';

export type TireOfferStatusBucketId = 'pending' | 'declined' | 'fulfillment' | 'completed';

type TireOfferStatusBucket = {
  id: TireOfferStatusBucketId;
  label: string;
  description: string;
  statuses: QuoteStatus[];
};

const TIRE_OFFER_STATUS_BUCKETS: readonly TireOfferStatusBucket[] = [
  {
    id: 'pending',
    label: 'Awaiting customer',
    description: 'Offer sent, waiting on customer to accept or decline.',
    statuses: [QuoteStatus.DRAFT, QuoteStatus.PENDING]
  },
  {
    id: 'declined',
    label: 'Declined',
    description: 'Customer rejected the offer or the offer was withdrawn.',
    statuses: [QuoteStatus.REJECTED, QuoteStatus.WITHDRAWN]
  },
  {
    id: 'fulfillment',
    label: 'In fulfillment',
    description: 'Customer accepted. Tires being ordered, prepared and scheduled for mounting.',
    statuses: [
      QuoteStatus.ACCEPTED,
      QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL,
      QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL,
      QuoteStatus.TO_BE_RETURNED_TO_SUPPLIER,
      QuoteStatus.INVENTORY_RECEIVED_IN_FULL,
      QuoteStatus.INVENTORY_RECEIVED_PARTIAL,
      QuoteStatus.TIRES_RECEIVED_AWAITING_MOUNTING_ON_RIM_AT_STORAGE_FACILITY,
      QuoteStatus.BOOKING_PROPOSAL_TIRE_MOUNT_SENT_TO_CUSTOMER,
      QuoteStatus.TIRE_MOUNTING_BOOKED
    ]
  },
  {
    id: 'completed',
    label: 'Completed',
    description: 'Tires mounted — handled.',
    statuses: [
      QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER,
      QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY,
      QuoteStatus.FULFILLED,
      QuoteStatus.FULFILLED_PARTIALLY
    ]
  }
] as const;

/**
 * Every status an offer reaches once the customer accepted it — the backend's `get_is_accepted` and the dashboard's
 * `acceptedOffers` (an item selected). Includes later cancelled, refunded and returned offers: customers usually
 * prepay, so the acceptance stands.
 */
export const ACCEPTED_QUOTE_STATUSES: readonly QuoteStatus[] = [
  ...TIRE_OFFER_STATUS_BUCKETS.filter((bucket) => bucket.id === 'fulfillment' || bucket.id === 'completed').flatMap(
    (bucket) => bucket.statuses
  ),
  QuoteStatus.CANCELLED,
  QuoteStatus.PARTIALLY_REFUNDED,
  QuoteStatus.REFUNDED,
  QuoteStatus.TO_BE_RETURNED_TO_SUPPLIER
];

/** Offers still under way: awaiting the customer, or accepted and not yet mounted. */
export const OPEN_TIRE_OFFER_STATUSES: readonly QuoteStatus[] = TIRE_OFFER_STATUS_BUCKETS.filter(
  (bucket) => bucket.id === 'pending' || bucket.id === 'fulfillment'
).flatMap((bucket) => bucket.statuses);

export const TIRE_OFFER_BUCKET_HEX: Record<TireOfferStatusBucketId, string> = {
  pending: '#EAB308',
  declined: '#EA5147',
  fulfillment: '#8661C1',
  completed: '#128667'
};

type StatusDistributionItem = { status: QuoteStatus; count: number };

export type BucketizedDistribution = {
  id: TireOfferStatusBucketId;
  label: string;
  description: string;
  count: number;
  sub: { status: QuoteStatus; label: string; count: number }[];
};

export const bucketizeStatusDistribution = (distribution: StatusDistributionItem[]): BucketizedDistribution[] => {
  const countsByStatus = new Map<QuoteStatus, number>();
  for (const item of distribution) {
    countsByStatus.set(item.status, (countsByStatus.get(item.status) ?? 0) + item.count);
  }

  return TIRE_OFFER_STATUS_BUCKETS.map((bucket) => {
    const sub = bucket.statuses
      .map((status) => ({
        status,
        label: QuoteStatusLabels[status],
        count: countsByStatus.get(status) ?? 0
      }))
      .filter((row) => row.count > 0);
    const count = sub.reduce((acc, row) => acc + row.count, 0);
    return {
      id: bucket.id,
      label: bucket.label,
      description: bucket.description,
      count,
      sub
    };
  });
};

export type OfferFunnelStageId = 'sent' | 'accepted' | 'mounting_booked' | 'tires_mounted';

export type OfferFunnelStage = {
  id: OfferFunnelStageId;
  label: string;
  count: number;
};

export const computeOfferFunnel = (
  distribution: StatusDistributionItem[],
  total: number,
  acceptedOffers: number
): OfferFunnelStage[] => {
  const buckets = bucketizeStatusDistribution(distribution);
  const completedCount = bucketCount(buckets, 'completed');
  const mountingBookedCount =
    statusCount(distribution, QuoteStatus.TIRE_MOUNTING_BOOKED) +
    statusCount(distribution, QuoteStatus.TIRES_RECEIVED_AWAITING_MOUNTING_ON_RIM_AT_STORAGE_FACILITY) +
    completedCount;

  return [
    { id: 'sent', label: 'Offer sent', count: total },
    { id: 'accepted', label: 'Accepted', count: acceptedOffers },
    { id: 'mounting_booked', label: 'Mounting booked or awaiting mounting at facility', count: mountingBookedCount },
    { id: 'tires_mounted', label: 'Tires mounted', count: completedCount }
  ];
};

const bucketCount = (buckets: BucketizedDistribution[], id: TireOfferStatusBucketId): number =>
  buckets.find((b) => b.id === id)?.count ?? 0;

const statusCount = (distribution: StatusDistributionItem[], status: QuoteStatus): number =>
  distribution.find((d) => d.status === status)?.count ?? 0;
